/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_TEMPLATE_TITLE,
  DEFAULT_TEMPLATE_WIDTH,
  DEFAULT_TEMPLATE_HEIGHT,
  MAX_TEMPLATE_WIDTH,
  MAX_TEMPLATE_HEIGHT,
} from "@utils/constant";

import Templates, {
  TemplateItemInfo,
  TemplateItemCategories,
} from "./templates";

import TemplateItem from "./TemplateItem";

type TemplateInfo = {
  title: string;
  width: number;
  height: number;
};

function AddTemplateModal({
  open = false,
  onCancel = () => {},
  onSubmit = () => {},
}: {
  open?: boolean;
  onCancel?: () => void;
  onSubmit?: (templateInfo: TemplateInfo) => void;
}) {
  const { t } = useTranslation();
  const cancelButtonRef = useRef(null);
  const [openTab, setOpenTab] = useState(
    Object.keys(Templates)[0] as TemplateItemCategories
  );
  const [selectedTemplateItem, setSelectedTemplateItem] = useState("");
  const [title, setTitle] = useState(DEFAULT_TEMPLATE_TITLE);
  const [width, setWidth] = useState(DEFAULT_TEMPLATE_WIDTH);
  const [height, setHeight] = useState(DEFAULT_TEMPLATE_HEIGHT);

  function onSelectTemplateItem(data: TemplateItemInfo) {
    const { width, height } = data;
    setWidth(width);
    setHeight(height);
    setSelectedTemplateItem(data.label);
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={onCancel}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block h-[720px] max-h-[calc(100vh-2rem)] w-full transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-6xl sm:align-middle">
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <div className="border-b border-gray-200 px-8 pt-8 pb-6">
                      <Dialog.Title
                        as="h1"
                        className="mb-2 text-2xl font-bold text-gray-900"
                      >
                        Create Template
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        Choose a template category and customize your dimensions
                      </p>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col px-8 py-6">
                      <div className="mb-6">
                        <nav className="flex gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1">
                          {Object.keys(Templates).map((category) => {
                            return (
                              <button
                                key={category}
                                className={`shrink-0 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all duration-200 ${
                                  openTab === category
                                    ? "bg-white text-gray-900 shadow-sm"
                                    : "text-gray-500 hover:text-gray-900"
                                }`}
                                onClick={() => {
                                  setOpenTab(
                                    category as TemplateItemCategories
                                  );
                                }}
                              >
                                {category}
                              </button>
                            );
                          })}
                        </nav>
                      </div>

                      <div
                        className="min-h-0 flex-1 overflow-y-auto pr-2"
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor: "#d1d5db #f3f4f6",
                        }}
                      >
                        <div className="pb-2">
                          <div className="grid auto-rows-min grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                            {Templates[openTab].map((templateItemInfo) => {
                              return (
                                <TemplateItem
                                  selected={
                                    templateItemInfo.label ===
                                    selectedTemplateItem
                                  }
                                  data={templateItemInfo}
                                  onSelect={onSelectTemplateItem}
                                  key={templateItemInfo.label}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full shrink-0 overflow-y-auto border-t border-gray-200 bg-gray-50 lg:w-80 lg:border-t-0 lg:border-l">
                    <div className="border-b border-gray-200 p-6">
                      <h3 className="mb-1 text-lg font-semibold text-gray-900">
                        {t("add_template_settings_title")}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {t("add_template_settings_body")}
                      </p>
                    </div>

                    <div className="space-y-6 p-6">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          {t("add_template_title_label")}
                        </label>
                        <input
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                          type="text"
                          placeholder={DEFAULT_TEMPLATE_TITLE}
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        />
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {t("add_template_width_label")}
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            type="number"
                            placeholder={t("add_template_width_placeholder")}
                            value={width}
                            onChange={(e) => setWidth(parseInt(e.target.value))}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {t("add_template_max_px", {
                              value: MAX_TEMPLATE_WIDTH,
                            })}
                          </p>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {t("add_template_height_label")}
                          </label>
                          <input
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                            type="number"
                            placeholder={t("add_template_height_placeholder")}
                            value={height}
                            onChange={(e) =>
                              setHeight(parseInt(e.target.value))
                            }
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            {t("add_template_max_px", {
                              value: MAX_TEMPLATE_HEIGHT,
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 rounded-b-xl border-t border-gray-200 bg-gray-50 px-8 py-4">
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={() => {
                      onCancel();
                    }}
                    ref={cancelButtonRef}
                  >
                    {t("common_cancel")}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-blue-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={() => {
                      onSubmit({
                        width,
                        height,
                        title,
                      });
                    }}
                  >
                    {t("add_template_create_button")}
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default AddTemplateModal;
